<template>
  <div>
    <div v-show="isAccesstable" class="group-detail">
      <h1 class="common__main-title">Final Review Sheet</h1>
      <div class="sub-title">
        <div v-if="lesson" class="row w-100">
          <div class="col"></div>
          <div class="col-8">
            {{ lesson.fiscalYear }} {{ lesson.semester.name }}
            {{ lesson.course.name }} Unit{{ lesson.unit }}
            {{ lesson.date.format('YYYY/MM/DD')}} {{ lesson.date | weekday }}
            Period{{ lesson.period }}
            {{ lesson.campus.name }}
            {{ lesson.booth.building }}-{{ lesson.booth.room }}-{{ lesson.booth.number }}
          </div>
          <div class="col"></div>
        </div>
      </div>

      <div v-if="selectedStudent" class="student-list mt-4">
        <button v-for="student in students" :key="student.id"
          :class="{ 'student-button': true, 'active': selectedStudent.id === student.id }"
          @click="selectedStudent = student">
          {{ student.name }}
        </button>
      </div>

      <ValidationObserver ref="observer">
        <div v-if="selectedStudent" class="common__main-contener mb-5">
          <div class="common__main-content">
            <div class="form-group h-auto"
              v-for="grade of grades" :key="grade.studentId"
              v-show="selectedStudent.id === grade.studentId">
              <ValidationProvider :rules="{ required: !grade.isLocked }" v-slot="{ errors }">
                <textarea class="form-control" rows="30"
                  :disabled="grade.isLocked || isConfirm"
                  v-model="grade.note" />
                <div class="text-danger">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>
          </div>
        </div>
      </ValidationObserver>

      <div v-if="lesson" class="d-flex justify-content-between">
        <template v-if="isConfirm">
          <b-button pill variant="secondary" @click="switchConfirm(false)">
            Back
          </b-button>
          <b-button pill variant="primary" @click="register">
            Send
          </b-button>
        </template>
        <template v-else>
          <b-button pill variant="secondary" :to="{ name:'Home' }">Back</b-button>

          <template v-if="grades.filter(g => g.isLocked).length === grades.length">
            <b-button v-if="isAcceptableInquiry" pill variant="primary" @click="acceptInquiry">
              Accept Evaluation
            </b-button>
            <b-button
            pill variant="primary"
            :to="{ name: 'ApplicationAdd' }"
            :disabled="!isEvaluable"
            >
              Apply inquiry
            </b-button>
          </template>
          <template v-else>
            <template v-if="isEvaluable">
              <b-button pill variant="primary" @click="temporarilySave">
                Temporarily saved
              </b-button>
              <b-button pill variant="primary" @click="switchConfirm(true)">
                Confirm
              </b-button>
            </template>
          </template>
        </template>
      </div>
    </div>
    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import moment, { Moment } from 'moment'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { FinalReviewSheet, FinalReviewSheetForRegister, StudentWithFinalReview } from '@/store/lessons/finalReviewSheet/types'
import { Student } from '@/store/students/types'
import Loading from '@/components/parts/modal/Loading.vue'
import { RootState } from '@/store/types'
import { EvaluationInquiryForAccept } from '@/store/applications/evaluationInquiries/types'
import { Lesson } from '@/store/lessons/types'

interface FinalReviewSheetGrade {
  isLocked: boolean
  studentId: string
  note: string
}

export default Vue.extend({
  components: {
    ValidationObserver,
    ValidationProvider,
    Loading,
  },
  filters: {
    weekday: (date: Moment) => date.format('ddd'),
  },

  data: (): {
    isLoading: boolean,
    isConfirm: boolean,
    selectedStudent: Student|null,
    grades: FinalReviewSheetGrade[],
    studentsWithFinalReview: StudentWithFinalReview[],
  } => ({
    isLoading: false,
    isConfirm: false,
    selectedStudent: null,
    grades: [],
    studentsWithFinalReview: [],
  }),
  computed: {
    frs(): FinalReviewSheet {
      return this.$store.getters.findFinalReviewSheetByLessonId(this.$route.params.lessonId)
    },
    lesson(): Lesson|undefined {
      return this.frs && this.frs.lesson
    },
    students(): Student[] {
      return _.chain(this.frs).get('students').map('student').value() || []
    },
    /**
     * アクセス権限チェック
     * @returns {boolean} ture: アクセスOK, false: アクセスNG
     */
    isAccesstable(): boolean {
      const { profile } = (this.$store.state as RootState).auth
      if (_.indexOf(['admin', 'senior'], profile?.authority) !== -1) {
        // 管理者、シニアはOK
        return true
      }

      // レッスンの教師か代講チューターであればOK
      return this.lesson?.teacher?.tutor.id === profile?.id
        || this.lesson?.substituteTutor?.tutor.id === profile?.id
    },

    /**
     * 評価可能かどうか判定
     * @return {boolean} true: 評価可能, false: 不可
     */
    isEvaluable(): boolean {
      if (_.isUndefined(this.lesson)) {
        return false
      }
      // 未来のレッスンは不可
      if (moment().format('YYYYMMDD') < this.lesson.date.clone().format('YYYYMMDD')) {
        return false
      }
      // 評価承認待ちの場合は不可
      if (_.filter(this.studentsWithFinalReview, { isWaitingApproved: true }).length > 0
          || _.filter(this.studentsWithFinalReview, { isApplication: true }).length > 0
      ) {
        return false
      }

      // レッスンの教師以外は不可
      const { profile } = (this.$store.state as RootState).auth
      if (
        profile?.id
        && (
          this.lesson?.teacher?.tutor.id === profile?.id
          || this.lesson?.substituteTutor?.tutor.id === profile?.id
        )
      ) {
        // ログインユーザのIDが取得でき、かつレッスンのチューターか代講チューターである場合
        return true
      }

      // 権限がseniorの場合は評価・申請可
      if (profile?.authority === 'senior') {
        return true
      }

      return false
    },

    /**
     * 評価申請後の修正承認可否
     * @return {boolean} true: 修正承認可能, false: 不可
     */
    isAcceptableInquiry(): boolean {
      if (this.students.length > _.filter(this.frs.students, { isLocked: true }).length) {
        // 全てロック状態出ない場合false
        return false
      }

      if (_.filter(this.frs.students, { isWaitingApproved: true }).length === 0) {
        // 評価承認待ちが存在しない場合はfalse
        return false
      }

      const { profile } = (this.$store.state as RootState).auth

      if (_.filter(this.frs.students, { applicantId: profile?.id }).length > 0) {
        // 申請者の場合はfalse
        return false
      }

      if (profile?.authority === 'senior') {
        // 権限がseniorの場合のみtrue
        return true
      }

      return false
    },
  },

  created() {
    this.isLoading = true
    this.$store.dispatch('getFinalReviewSheetByLessonId', { lessonId: this.$route.params.lessonId })
      .finally(() => {
        // アクセス権限チェック
        if (!this.isAccesstable) {
          this.$router.push({ name: 'Home' })
          return
        }

        if (this.lesson?.fiscalYear && this.lesson.fiscalYear <= 2023) {
          this.$router.push({ name: 'OldLMSData' })
          return
        }

        this.isLoading = false
        this.selectedStudent = _.first(this.students) || null
        this.grades = this.frs.students.map(
          ({ student, isLocked, forEdit }): FinalReviewSheetGrade => _.cloneDeep(({
            studentId: student.id,
            isLocked,
            note: forEdit.note,
          })),
        )
        this.studentsWithFinalReview = _.chain(this.frs)
          .get('students').map(_.cloneDeep)
          .value() || []
      })
  },
  methods: {
    /**
     * 入力画面と確認画面の切り替え
     * @param {boolean} isConfirm true: 確認画面、 false: 入力画面
     */
    async switchConfirm(isConfirm: boolean) {
      if (this.lesson?.fiscalYear && this.lesson.fiscalYear <= 2023) {
        this.$bvModal.msgBoxOk('Error - This data from 2021 to 2023 cannot be registered.');
        return;
      }
      if (!await (this.$refs.observer as any).validate()) {
        this.$bvModal.msgBoxOk('Please review all students')
        return
      }

      this.selectedStudent = _.first(this.students) || null
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      this.isConfirm = isConfirm
    },

    /**
     * 一時保存
     */
    async temporarilySave() {
      if (this.isLoading) {
        return
      }

      try {
        this.isLoading = true
        await Promise.all([
          ...this.grades
            // ロック中のデータは対象外
            .filter((grade) => !grade.isLocked)
            .map((grade) => {
              const finalReviewSheet: FinalReviewSheetForRegister = {
                lessonId: this.$route.params.lessonId,
                studentId: grade.studentId,
                note: grade.note,
              }

              return this.$store.dispatch('temporarilySaveFinalReviewSheet', finalReviewSheet)
            }),
        ])

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !').then(() => { this.$router.go(0) })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },

    /**
     * 登録
     */
    async register() {
      if (this.isLoading) {
        return
      }

      try {
        this.isLoading = true
        await Promise.all(this.grades
          // ロック中のデータは対象外
          .filter((grade) => !grade.isLocked)
          .map((grade): Promise<void> => {
            const finalReviewSheet: FinalReviewSheetForRegister = {
              lessonId: this.$route.params.lessonId,
              studentId: grade.studentId,
              note: grade.note,
            }

            return this.$store.dispatch('registerFinalReviewSheet', finalReviewSheet)
          }))

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !').then(() => { this.$router.go(0) })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },

    /**
     * 評価申請問い合わせの修正承認
     */
    async acceptInquiry() {
      if (!this.isAcceptableInquiry || this.isLoading) {
        return
      }

      if (await this.$bvModal.msgBoxConfirm('Are you sure accept evaluation from inquiry application?') !== true) {
        return
      }

      try {
        this.isLoading = true
        await Promise.all(this.frs.students
          .filter((s) => s.isWaitingApproved)
          .map((s) => {
            const params: EvaluationInquiryForAccept = {
              lessonId: this.$route.params.lessonId,
              studentId: s.student.id,
            }
            return this.$store.dispatch('acceptFrsEvaluationWithInquiry', params)
          }))

        this.isLoading = false
        this.$bvModal.msgBoxOk('Success !').then(() => { this.$router.go(0) })
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },
  },
})
</script>

<style scoped>

.sub-title {
  padding:10px;
  text-align: center;
  background: #DDEAF6;
  display: flex;
  justify-content: space-between;
  font-size: 16px;
}

.sub-title a{
  font-size: 14px;
}

.preparation-data{
  text-align: right;
  margin: 10px;
  font-size: 14px;
  margin-right: 35px;
}

.preparation-data a {
  font-size: 15px;
}

.student-list{
  display: flex;
  justify-content:space-between;
  margin-bottom: 10px;
}

.student-button{
  padding: 15px;
  text-align: center;
  background-color: #E7E6E6;
  flex-basis:100%;
  margin: 0px 7px;
  border-style: none;
  cursor: pointer;
}

.active {
  background-color: #9DC3E6;
}

input[type="text"], select{
  padding: 5px;
  border: 1px solid #AAAFB6;
  border-radius: 30px;
  width:100%;
}

</style>
